import { tokens } from "lib/colors"
import { memo, useContext, useEffect, useState } from "react"
import { LanguageContext } from "context/LanguageContext"
import { motion } from "framer-motion"
import Button from "lib/components/Button"
import Icons from "lib/components/Icons"
import Text from "lib/components/Text"
import { handleChar, optimizeImageCloudinary } from "lib/helpers"
import { gtmEvent } from "lib/gtm"
import { isDesktop } from "react-device-detect"
import Image from "next/image"
import ColoredText from "@components/shared-components/ColoredText"

export const scrollToTargetSection = (value) => {
  const targetSection = document.getElementById("form_leads_section")
  if (targetSection) {
    const scrollOptions = {
      top: targetSection.offsetTop,
      behavior: "smooth" as ScrollBehavior,
    }
    window.scroll(scrollOptions)
  }

  gtmEvent("cs_get_started", {
    position: value,
  })
}

interface HeroSectionProps {
  listData: Array<{
    title: string
    description: string
  }>
}

const LIST_ICONS = ["academic", "squares-2x2", "briefcase", "check-badge"]

const slidesData = [
  {
    id: 1,
    title: "In-house program",
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675978/corporate_solution/inhouse-cs.webp",
  },
  {
    id: 2,
    title: "Mentoring & coaching",
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675978/corporate_solution/mentoring-coaching-cs.webp",
  },
  {
    id: 3,
    title: "Educational outing",
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675977/corporate_solution/educational-outing-cs.webp",
  },
  {
    id: 4,
    title: "Training Management System",
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675971/corporate_solution/tms-cs.webp",
  },
  {
    id: 5,
    title: "Speaker",
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675978/corporate_solution/speaker-cs.webp",
  },
]

const cardVariants = {
  animate: (index) => ({
    x: 0,
    y: index * (isDesktop ? 15 : 10),
    scale: 1 - index * 0.05,
    zIndex: slidesData.length - index,
    opacity: 1,
    transition: { type: "spring", stiffness: 100, damping: 20 },
  }),
}

const StackingCardAnimation = () => {
  const [cards, setCards] = useState(slidesData)

  useEffect(() => {
    const interval = setInterval(() => {
      setCards((prev) => [...prev.slice(1), prev[0]])
    }, 3000)
    return () => clearInterval(interval)
  }, [])

  return (
    <>
      {cards.map((card, index) => (
        <motion.div
          key={card.id}
          className="absolute z-10 mb-6 lg:mb-8 w-full max-w-[328px] h-[193px] lg:max-w-[571px] lg:h-[335px] border-2 border-primary-border bg-white rounded-xl p-3.5"
          custom={index}
          variants={cardVariants}
          initial={false}
          animate="animate">
          <div className="relative aspect-video w-full h-full rounded">
            <div className="absolute left-0 top-0 z-10 rounded-tl-xl rounded-br-xl bg-primary px-3 py-1 text-xs lg:text-xl font-semibold text-white">
              {card.title}
            </div>
            <Image
              fill
              priority={card.id === slidesData[0].id}
              alt={card.title}
              className="rounded-xl object-cover"
              src={optimizeImageCloudinary(card.image, "high", "w_1000")}
              sizes="(max-width: 1023px) 296px, 535px"
            />
          </div>
        </motion.div>
      ))}
    </>
  )
}

function Hero({ listData }: HeroSectionProps) {
  const { useTranslation: t, language } = useContext(LanguageContext)

  return (
    <section className="bg-primary w-full">
      <div className="container p-4 lg:p-0 mx-auto h-full min-h-[39.875rem] flex flex-col justify-center gap-5 lg:gap-[52px]">
        <div className="flex flex-col lg:flex-row gap-5 lg:gap-[66px] items-center justify-normal lg:justify-between">
          <div className="flex flex-col gap-3 lg:gap-6 items-center lg:items-start justify-center lg:justify-start w-full lg:w-1/2">
            <div className="flex flex-row items-center gap-1 lg:gap-2">
              <div className="w-[28px] h-[28px] lg:w-[30px] lg:h-[30px] p-1 flex items-center justify-center rounded bg-white">
                <Icons pathName="icon-corporate-solution" width={20} height={20} />
              </div>
              <Text
                color="light"
                weight="semibold"
                customClass="text-center lg:text-start text-xs lg:text-xl">
                {t("corporate_solution").toUpperCase()}
              </Text>
            </div>
            <div className="flex flex-col gap-3.5">
              <h1 className="m-0 font-bold text-white text-center lg:text-start text-2xl lg:text-5xl lg:max-w-[550px]">
                <ColoredText
                  text={t("take_your_employee_to_the_next_level")}
                  language={language}
                  keywords={{
                    EN: ["next level"],
                    ID: ["level selanjutnya"],
                  }}
                />
              </h1>
              <Text color="light" customClass="text-center lg:text-start text-sm lg:text-xl">
                {t(
                  "accelerate_your_business_transformation_by_building_employee_skills_with_pasartrainer",
                )}
              </Text>
            </div>
            <div className="hidden lg:flex flex-row items-center gap-4">
              <Button
                onClick={() => {
                  scrollToTargetSection("top page")
                  gtmEvent("ce_section_engagement", {
                    section_name: t("corporate_solution"),
                    button_name: t("FREE_consultation"),
                  })
                }}
                color="white"
                colorText="primary"
                variant="contained"
                fontWeight="semibold"
                customClass="w-fit h-[44px] !text-base !shadow-none">
                {t("contact_us")}
              </Button>
              <Button
                href="/programs/all"
                onClick={() => {
                  gtmEvent("ce_section_engagement", {
                    section_name: t("corporate_solution"),
                    button_name: t("see_all_program"),
                  })
                }}
                color="white"
                variant="outlined"
                customClass="w-fit max-w-[200px] h-[44px] !text-base !font-semibold !tracking-normal">
                {t("see_all_programs")}
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-center w-full lg:w-1/2">
            <div className="relative flex items-center justify-center w-full">
              <div className="relative w-full max-w-[171px] h-[239px] lg:max-w-[284px] lg:h-[383px]" />
              <StackingCardAnimation />
            </div>
          </div>
          <div className="flex flex-row items-center gap-4 lg:hidden">
            <Button
              onClick={() => {
                scrollToTargetSection("top page")
                gtmEvent("ce_section_engagement", {
                  section_name: t("corporate_solution"),
                  button_name: t("contact_us"),
                })
              }}
              color="white"
              colorText="primary"
              variant="contained"
              customClass="w-[164px] h-[44px] !text-sm !font-semibold !shadow-none">
              {t("contact_us")}
            </Button>
            <Button
              href="/programs/all"
              onClick={() => {
                gtmEvent("ce_section_engagement", {
                  section_name: t("corporate_solution"),
                  button_name: t("see_all_program"),
                })
              }}
              color="white"
              variant="outlined"
              customClass="w-fit max-w-[200px] h-[44px] !text-sm !font-semibold !tracking-normal">
              {t("see_all_programs")}
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-[25px] lg:gap-12 py-3 px-3.5 lg:p-4 w-full h-fit lg:h-[116px] bg-white rounded-2xl mb-4 lg:mb-0 relative">
          {listData?.map(({ title, description }, indexListData) => (
            <div
              key={indexListData}
              className="flex flex-col gap-1 items-center justify-center relative">
              <div className="flex flex-row items-center gap-1">
                <Icons
                  iconName={LIST_ICONS[indexListData]}
                  styles="w-6 h-6 lg:w-8 lg:h-8"
                  type="stroke"
                  color={tokens.primary.DEFAULT}
                />
                <Text
                  color="primary"
                  weight="medium"
                  customClass="text-center lg:text-start text-sm lg:text-xl">
                  {description}
                </Text>
              </div>
              <Text customClass="text-center lg:text-start text-sm lg:text-base !text-black">
                {t(`${handleChar({ value: title, replacement: "_" }).toLocaleLowerCase()}`)}
              </Text>
              {indexListData < listData.length - 1 && (
                <div className="hidden lg:block absolute -right-6 top-1/2 transform -translate-y-1/2 w-px h-full bg-primary-border" />
              )}
              {/* Mobile dividers */}
              {/* Vertical divider between columns 1-2 and 3-4 on mobile */}
              {(indexListData === 0 || indexListData === 2) && (
                <div className="lg:hidden absolute -right-3 top-1/2 transform -translate-y-1/2 w-px h-12 bg-primary-border" />
              )}
            </div>
          ))}
          {/* Mobile horizontal divider - positioned relative to the grid container */}
          <div className="lg:hidden absolute top-1/2 left-0 right-0 h-px bg-primary-border transform -translate-y-1/2 mx-4" />
        </div>
      </div>
    </section>
  )
}

export default memo(Hero)
